import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { pmsAPI } from '../../services/api';
import './PmsWebhookPanel.css';
import { formatDateTime } from '../../utils/dateFormat';

const STATUS_CLASS = {
    processed: 'pwp-status--ok',
    failed:    'pwp-status--err',
    unmapped:  'pwp-status--warn',
    duplicate: 'pwp-status--muted',
    received:  'pwp-status--muted',
};

function copyText(text, label) {
    if (!navigator.clipboard) {
        toast.error('Copy is not available here — select the text and copy it manually');
        return;
    }
    navigator.clipboard.writeText(text)
        .then(() => toast.success(`${label} copied`))
        .catch(() => toast.error('Copy failed — select the text and copy it manually'));
}

/**
 * Per-branch webhook setup for a PMS that pushes events to us (FIAS bridge,
 * or any provider with webhook support): the URL to post to, the property
 * code it must send, and the branch's HMAC secret.
 *
 * The secret is generated by the server and shown here only right after
 * "Generate" — it can't be read back later, only replaced.
 *
 * Reads the SAVED branch settings, not the unsaved form, so `formPropertyCode`
 * is only used to warn when the form has an unsaved property code change.
 */
const PmsWebhookPanel = ({ branchId, provider, formPropertyCode }) => {
    const isFias = provider === 'fias';
    const [config, setConfig]         = useState(null);
    const [loading, setLoading]       = useState(false);
    const [loadError, setLoadError]   = useState(null);
    const [newSecret, setNewSecret]   = useState(null);
    const [generating, setGenerating] = useState(false);

    const load = useCallback(async () => {
        if (!branchId) return;
        setLoading(true);
        setLoadError(null);
        try {
            const res = await pmsAPI.getWebhookConfig(branchId, provider);
            setConfig(res.data.data);
        } catch (e) {
            setLoadError(e.response?.data?.message || 'Could not load webhook settings');
        } finally {
            setLoading(false);
        }
    }, [branchId, provider]);

    useEffect(() => {
        setNewSecret(null);
        setConfig(null);
        load();
    }, [load]);

    const generate = async () => {
        if (config?.secretSet && !window.confirm(
            'Generate a new webhook secret for this branch?\n\nThe current secret stops working immediately — '
            + (isFias ? 'update WEBHOOK_SECRET on the FIAS bridge right after.' : 'update it on the PMS right after.'),
        )) return;
        setGenerating(true);
        try {
            const res = await pmsAPI.generateWebhookSecret(branchId, provider);
            setNewSecret(res.data.data.secret);
            toast.success(res.data.message);
            load();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Could not generate a secret');
        } finally {
            setGenerating(false);
        }
    };

    const title = isFias ? 'FIAS Bridge Webhook' : 'Webhook (push events)';

    if (!branchId) {
        return (
            <div className="pwp">
                <div className="pwp-title"><i className="fas fa-satellite-dish"></i> {title}</div>
                <p className="input-hint">Save this branch first — its webhook can be set up once it exists.</p>
            </div>
        );
    }

    const savedCode = config?.propertyCode || '';
    const unsavedCode = formPropertyCode !== undefined && (formPropertyCode || '').trim() !== savedCode;
    const envSnippet = config && [
        `PROPERTY_CODE=${savedCode}`,
        `WEBHOOK_URL=${config.webhookUrl}`,
        `WEBHOOK_SECRET=${newSecret || '<generate a secret above, then paste it here>'}`,
    ].join('\n');

    return (
        <div className="pwp">
            <div className="pwp-header">
                <div className="pwp-title"><i className="fas fa-satellite-dish"></i> {title}</div>
                <button type="button" className="pwp-link" onClick={load} disabled={loading}>
                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i> Refresh
                </button>
            </div>
            <p className="pwp-sub">
                {isFias
                    ? 'The FIAS bridge at this hotel posts check-in / check-out events here. Each branch has its own Property Code and secret.'
                    : 'Optional — only needed if this PMS pushes events to HotWiFi. Each branch has its own secret.'}
            </p>

            {loadError && <p className="pwp-error">{loadError}</p>}

            {config && (
                <>
                    <div className="pwp-row">
                        <span className="pwp-label">Webhook URL</span>
                        <code className="pwp-value">{config.webhookUrl}</code>
                        <button type="button" className="pwp-copy" onClick={() => copyText(config.webhookUrl, 'Webhook URL')}>
                            <i className="fas fa-copy"></i>
                        </button>
                    </div>

                    <div className="pwp-row">
                        <span className="pwp-label">Property Code</span>
                        <code className="pwp-value">{savedCode || '— not saved yet —'}</code>
                    </div>
                    {unsavedCode && (
                        <p className="pwp-warn">
                            <i className="fas fa-exclamation-triangle"></i> The Property Code above has unsaved changes — save the branch before setting up the bridge.
                        </p>
                    )}

                    <div className="pwp-row">
                        <span className="pwp-label">Secret</span>
                        <span className={`pwp-badge ${config.secretSet ? 'pwp-badge--ok' : 'pwp-badge--warn'}`}>
                            {config.secretSet ? 'Set' : 'Not set — unsigned events are accepted'}
                        </span>
                        <button
                            type="button"
                            className="pwp-btn"
                            onClick={generate}
                            disabled={generating || !savedCode}
                            title={savedCode ? '' : 'Save the Property Code first'}
                        >
                            <i className={`fas ${generating ? 'fa-spinner fa-spin' : 'fa-key'}`}></i>
                            {config.secretSet ? ' Generate new secret' : ' Generate secret'}
                        </button>
                    </div>

                    {newSecret && (
                        <div className="pwp-secret">
                            <div className="pwp-secret-head">
                                <i className="fas fa-lock"></i> New secret — copy it now, it won&apos;t be shown again
                            </div>
                            <div className="pwp-row">
                                <code className="pwp-value pwp-value--wrap">{newSecret}</code>
                                <button type="button" className="pwp-copy" onClick={() => copyText(newSecret, 'Secret')}>
                                    <i className="fas fa-copy"></i>
                                </button>
                            </div>
                        </div>
                    )}

                    {isFias && (
                        <div className="pwp-env">
                            <div className="pwp-env-head">
                                <span>Bridge <code>.env</code> for this branch</span>
                                <button type="button" className="pwp-link" onClick={() => copyText(envSnippet, '.env')}>
                                    <i className="fas fa-copy"></i> Copy
                                </button>
                            </div>
                            <pre className="pwp-pre">{envSnippet}</pre>
                            {!newSecret && config.secretSet && (
                                <p className="input-hint">
                                    The current secret can&apos;t be shown again. If the bridge doesn&apos;t have it, generate a new one.
                                </p>
                            )}
                        </div>
                    )}

                    <div className="pwp-events">
                        <div className="pwp-label">Recent webhooks</div>
                        {config.recentEvents.length === 0 ? (
                            <p className="input-hint">None received for this branch yet.</p>
                        ) : (
                            <ul className="pwp-event-list">
                                {config.recentEvents.map(ev => (
                                    <li key={ev.eventId} className="pwp-event">
                                        <span className="pwp-event-time">{formatDateTime(ev.receivedAt)}</span>
                                        <span className="pwp-event-type">{ev.eventType}</span>
                                        <span className="pwp-event-room">
                                            {ev.oldRoomNumber ? `${ev.oldRoomNumber} → ${ev.roomNumber}` : (ev.roomNumber || '—')}
                                        </span>
                                        <span className={`pwp-status ${STATUS_CLASS[ev.status] || ''}`}>{ev.status}</span>
                                        {ev.error && <span className="pwp-event-error">{ev.error}</span>}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default PmsWebhookPanel;
